import { Component } from '@theme/component';

/**
 * Previous / next buttons and a progress line for The Making's scroll layout.
 *
 * The track is a native scroll-snap row, so it already works with touch,
 * trackpad and keyboard without this module. This only reveals the buttons
 * (hidden in the markup), moves one card per click, and keeps the buttons'
 * disabled state and the progress line in sync with the scroll position.
 */
class KpMakingScroller extends Component {
  requiredRefs = ['track', 'previous', 'next'];

  #abortController = new AbortController();
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    const { track, previous, next } = this.refs;
    const { signal } = this.#abortController;

    // Nothing to scroll: leave the buttons hidden
    if (track.scrollWidth - track.clientWidth < 2) return;

    this.dataset.ready = 'true';
    previous.hidden = false;
    next.hidden = false;

    previous.addEventListener('click', () => this.#step(-1), { signal });
    next.addEventListener('click', () => this.#step(1), { signal });
    track.addEventListener('scroll', () => this.#schedule(), { passive: true, signal });
    window.addEventListener('resize', () => this.#schedule(), { signal });

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    cancelAnimationFrame(this.#frame);
  }

  /** @param {number} direction - -1 for previous, 1 for next */
  #step(direction) {
    const { track } = this.refs;
    const card = track.firstElementChild;
    if (!(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (card.offsetWidth + gap) });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const { track, previous, next, progress } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.min(Math.max(track.scrollLeft, 0), max);

    previous.disabled = position <= 1;
    next.disabled = position >= max - 1;

    if (progress instanceof HTMLElement) {
      // Thumb width = visible share of the row; its offset follows the scroll
      const visible = track.clientWidth / track.scrollWidth;
      progress.style.setProperty('--kp-making-thumb', String(visible));
      progress.style.setProperty('--kp-making-offset', String(max > 0 ? (position / max) * (1 - visible) : 0));
    }
  }
}

if (!customElements.get('kp-making-scroller')) {
  customElements.define('kp-making-scroller', KpMakingScroller);
}
